import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Authorization } from "../../../Context/Authorization";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import { logActivity } from "../../../utils/LogActivity";
import Preloader from "../../../shared/PreLoaders";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const fieldWrap = { marginBottom: 16 };
const tableHeaderStyle = {
  padding: "12px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  color: "#2D3748",
  borderBottom: "1px solid #E2E8F0",
};

const tableCellStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #EDF2F7",
  fontSize: 13,
};
const ETVisitorsForm = () => {
  const Api = process.env.REACT_APP_API;
  const { userId } = useContext(AuthContext);

  const [ecoTourisms, setEcoTourisms] = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    ecoTourismId: "",
    date: "",
    adults: "",
    children: "",
    visitorCounts: {},
    variableAmounts: {},
    descriptions: {},
    totalAmount: 0,
    edcWorkersEngaged: "",
    roomsOccupied: "",
  });

  useEffect(() => {
    fetchEcoTourisms();
  }, []);

  useEffect(() => {
    if (formData.ecoTourismId) {
      fetchFees(formData.ecoTourismId);
    } else {
      setFees([]);
    }
  }, [formData.ecoTourismId]);

  const fetchEcoTourisms = async () => {
    try {
      const res = await axios.get(
        `${Api}/api/eco-tourism`,
        Authorization()
      );
      setEcoTourisms(res.data.data || []);
    } catch {
      setError("Failed to load Eco Tourism list");
    }
  };

  const fetchFees = async (ecoTourismId) => {
    try {
      const res = await axios.get(
        `${Api}/api/eco-tourism-fees/${ecoTourismId}`,
        Authorization()
      );

      const feeData = res.data.fees || [];
      setFees(feeData);

      const visitorCounts = {};
      const variableAmounts = {};
      const descriptions = {};

      feeData.forEach((fee) => {
        visitorCounts[fee._id] = "";
        variableAmounts[fee._id] = "";
        descriptions[fee._id] = "";
      });

      setFormData((prev) => ({
        ...prev,
        visitorCounts,
        variableAmounts,
        descriptions,
        totalAmount: 0,
      }));
    } catch {
      toast.error("Failed to load fee types");
    }
  };

  const recalculateTotal = (counts, variables) => {
    let total = 0;

    fees.forEach((fee) => {
      if (fee.feeMode === "fixed") {
        total += Number(counts[fee._id] || 0) * Number(fee.amount || 0);
      } else {
        total += Number(variables[fee._id] || 0);
      }
    });

    return total;
  };

  const handleFeeCountChange = (feeId, value) => {
    const updatedCounts = {
      ...formData.visitorCounts,
      [feeId]: value,
    };

    setFormData((prev) => ({
      ...prev,
      visitorCounts: updatedCounts,
      totalAmount: recalculateTotal(
        updatedCounts,
        formData.variableAmounts
      ),
    }));
  };

  const handleVariableAmountChange = (feeId, value) => {
    const updatedVariables = {
      ...formData.variableAmounts,
      [feeId]: value,
    };

    setFormData((prev) => ({
      ...prev,
      variableAmounts: updatedVariables,
      totalAmount: recalculateTotal(
        formData.visitorCounts,
        updatedVariables
      ),
    }));
  };

  const handleDescriptionChange = (feeId, value) => {
    setFormData((prev) => ({
      ...prev,
      descriptions: {
        ...prev.descriptions,
        [feeId]: value,
      },
    }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      ecoTourismId: "",
      date: "",
      adults: "",
      children: "",
      visitorCounts: {},
      variableAmounts: {},
      descriptions: {},
      totalAmount: 0,
      edcWorkersEngaged: "",
      roomsOccupied: "",
    });

    setFees([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        ecoTourismId: formData.ecoTourismId,
        date: formData.date,

        visitorSummary: {
          adults: Number(formData.adults || 0),
          children: Number(formData.children || 0),
          totalVisitors:
            Number(formData.adults || 0) +
            Number(formData.children || 0),
        },

        visitorDetails: fees.map((fee) => ({
          feeId: fee._id,
          feeType: fee.feeType,
          feeMode: fee.feeMode,
          amount:
            fee.feeMode === "fixed"
              ? Number(fee.amount)
              : Number(formData.variableAmounts[fee._id] || 0),

          count:
            fee.feeMode === "fixed"
              ? Number(formData.visitorCounts[fee._id] || 0)
              : 1,

          total:
            fee.feeMode === "fixed"
              ? Number(formData.visitorCounts[fee._id] || 0) *
              Number(fee.amount)
              : Number(formData.variableAmounts[fee._id] || 0),

          description:
            formData.descriptions[fee._id] || "",
        })),

        totalAmount: Number(formData.totalAmount),
        edcWorkersEngaged: Number(formData.edcWorkersEngaged || 0),
        roomsOccupied: Number(formData.roomsOccupied || 0),
      };

      await axios.post(
        `${Api}/api/eco-tourism-table`,
        payload,
        Authorization()
      );

      toast.success("Eco Tourism visitor entry added successfully");

      await logActivity(
        userId,
        "Created ET Visitors",
        "ET Visitors Form Page",
        Api
      );

      resetForm();
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed to save data"
      );
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = (e) => (e.target.style.borderColor = "#4CAF50");
  const blurStyle = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>ET Visitors Entry</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Eco Tourism</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>ET Visitors Entry</span>
        </div>
      </div>

      {/* ── Form Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
            <i className="mdi mdi-account-group" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>New ET Visitors Registration Form</span>
        </div>

        <div style={{ padding: "24px 28px" }}>
          {error && (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: 8, fontSize: 12, color: "#E53E3E", fontWeight: 600 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Eco tourism */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Eco Tourism</label>
              <select
                id="ecoTourismId"
                value={formData.ecoTourismId}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="">Select Eco Tourism</option>
                {ecoTourisms.map((eco) => (
                  <option key={eco._id} value={eco._id}>
                    {eco.ecoTourismName}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Date</label>
              <input
                id="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>

            {/* Visitor summary */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                marginBottom: 20,
              }}
            >
              <div>
                <label style={labelStyle}>Adult Visitors</label>
                <input
                  id="adults"
                  type="number"
                  min="0"
                  value={formData.adults}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Child Visitors</label>
                <input
                  id="children"
                  type="number"
                  min="0"
                  value={formData.children}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Fees */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Fee Type</th>
                  <th style={tableHeaderStyle}>Rate</th>
                  <th style={tableHeaderStyle}>Count / Amount</th>
                  <th style={tableHeaderStyle}>Description</th>
                  <th style={tableHeaderStyle}>Total</th>
                </tr>
              </thead>

              <tbody>
                {fees.map((fee) => {
                  const count = Number(formData.visitorCounts[fee._id] || 0);
                  const variable = Number(
                    formData.variableAmounts[fee._id] || 0
                  );

                  const total =
                    fee.feeMode === "fixed"
                      ? count * fee.amount
                      : variable;

                  return (
                    <tr key={fee._id}>
                      <td style={tableCellStyle}>{fee.feeType}</td>

                      <td style={tableCellStyle}>
                        {fee.feeMode === "fixed"
                          ? `₹${fee.amount}`
                          : "Variable"}
                      </td>

                      <td style={tableCellStyle}>
                        {fee.feeMode === "fixed" ? (
                          <input
                            type="number"
                            min="0"
                            value={formData.visitorCounts[fee._id]}
                            onChange={(e) =>
                              handleFeeCountChange(
                                fee._id,
                                e.target.value
                              )
                            }
                            style={inputStyle}
                          />
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={formData.variableAmounts[fee._id]}
                            onChange={(e) =>
                              handleVariableAmountChange(
                                fee._id,
                                e.target.value
                              )
                            }
                            style={inputStyle}
                          />
                        )}
                      </td>

                      <td style={tableCellStyle}>
                        <input
                          type="text"
                          placeholder="Description"
                          value={formData.descriptions[fee._id] || ""}
                          onChange={(e) =>
                            handleDescriptionChange(
                              fee._id,
                              e.target.value
                            )
                          }
                          style={inputStyle}
                        />
                      </td>

                      <td style={tableCellStyle}>₹{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* totals */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Total Amount</label>
              <input
                value={formData.totalAmount}
                readOnly
                style={{ ...inputStyle, background: "#F7FAFC" }}
              />
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>EDC Workers Engaged</label>
              <input
                id="edcWorkersEngaged"
                type="number"
                value={formData.edcWorkersEngaged}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Rooms Occupied</label>
              <input
                id="roomsOccupied"
                type="number"
                value={formData.roomsOccupied}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button type="submit" disabled={loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", opacity: loading ? 0.7 : 1 }}>
                <i className="mdi mdi-check" /> {loading ? "Saving..." : "Submit"}
              </button>
              <button type="reset"
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ETVisitorsForm;
